"use client";

import posthog from "posthog-js";
import { createMerchantAnalytics } from "./analytics";
import {
  sanitizeAnalyticsEventProperties,
  sanitizeAnalyticsPersonProperties,
  sanitizeAnalyticsUrl,
} from "./privacy";

export const merchantAnalyticsEnabled =
  typeof window !== "undefined" &&
  Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY) &&
  (process.env.NODE_ENV === "production" || process.env.POSTHOG_DEV === "true");

const analytics = createMerchantAnalytics(posthog, {
  enabled: merchantAnalyticsEnabled,
  environment: process.env.NODE_ENV,
  getLocation: () => window.location,
});

if (merchantAnalyticsEnabled && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    rageclick: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    save_referrer: true,
    save_campaign_params: true,
    cross_subdomain_cookie: false,
    // The authenticated identity provider starts replay only on eligible routes.
    disable_session_recording: true,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
      // Explicit values override blanket masking in the project settings.
      maskTextSelector: "",
      maskAllElementAttributes: false,
      blockSelector: "",
      recordHeaders: false,
      recordBody: false,
      captureJsonLd: false,
      maskCapturedNetworkRequestFn: (request) => {
        const name = sanitizeAnalyticsUrl(request.name);
        return name ? { ...request, name } : null;
      },
    },
    before_send: (event) => {
      if (
        event?.event === "$snapshot" &&
        !analytics.canCaptureReplaySnapshot(event.properties.$session_id)
      ) {
        return null;
      }
      if (event) {
        const merchantOrigin = window.location.origin;
        event.properties = sanitizeAnalyticsEventProperties(
          event.event,
          event.properties,
          merchantOrigin,
        );
        if (event.$set)
          event.$set = sanitizeAnalyticsPersonProperties(
            event.$set,
            merchantOrigin,
          );
        if (event.$set_once)
          event.$set_once = sanitizeAnalyticsPersonProperties(
            event.$set_once,
            merchantOrigin,
          );
      }
      return event;
    },
  });
}

export const trackMerchantEvent = analytics.track;
export const setMerchantAnalyticsContext = analytics.setContext;
export const identifyMerchantShop = analytics.identifyShop;
export const setMerchantOnboardingContext = analytics.setOnboardingContext;
export const trackMerchantPageView = analytics.pageView;
export const setMerchantSessionRecordingEnabled =
  analytics.setSessionRecordingEnabled;

export const resetMerchantAnalytics = analytics.reset;
